import _ from "lodash"
import React, { useState } from "react"
import { Flight } from "../api/Flight"
import FlightAvailability, { FlightMode, ViewMode } from "./FlightAvailability"
import { ValueLabel } from "./generic/fields"
import CustomModal from "./modal"
import TransactionFlight from "./transaction/TransactionFlight"
import {
  EditMode,
  handleHasFlightsWithParkingLoc,
  handleHasFlightsWithServiceTypeAcReg,
  PageGroup
} from "../common/transaction"
import { AirportSeasonConfigurations } from "../api/AirportSeasonConfiguration"
import { CodeType } from "./AirportSelector"
import { BtnEvent } from "../api/EventTypes"
import TextField from "./generic/form/TextField"
import TextArea from "./generic/form/TextArea"

interface Props {
  transId: number
  grcMode: string
  flight: Flight
  editMode: EditMode
  pageGroup: PageGroup
  airportSeasons: AirportSeasonConfigurations
  codeType: CodeType
  arrMatrix: string
  depMatrix: string
  isHandlingPage: boolean
  onSave: (flight: Flight) => void
  onCancel: () => void
  onChangeAvailability: (
    viewMode: ViewMode,
    flightMode: FlightMode,
    flight: Flight
  ) => void
}

const FlightAvailabilityPanel: React.FunctionComponent<Props> = ({
  flight,
  transId,
  grcMode,
  airportSeasons,
  editMode,
  pageGroup,
  codeType,
  arrMatrix,
  depMatrix,
  isHandlingPage,
  onSave,
  onCancel,
  onChangeAvailability
}) => {
  const [availabilityFlight, setAvailabilityFlight] = useState<Flight>(flight)

  const handleSaveChanges = () => {
    onSave(availabilityFlight)
  }

  const handleChangeTime = (
    val: string,
    flightMode: FlightMode,
    viewMode: ViewMode
  ) => {
    const updAvailFlight = _.cloneDeep(availabilityFlight)
    if (flightMode === FlightMode.ARRIVAL) {
      updAvailFlight.clearedTimeArr = val
    }
    if (flightMode === FlightMode.DEPARTURE) {
      updAvailFlight.clearedTimeDep = val
    }
    setAvailabilityFlight(updAvailFlight)
    onChangeAvailability(viewMode, flightMode, updAvailFlight)
  }

  const handleGetTimeOpts = (chosenTime: string): Array<ValueLabel> => {
    const opts = []
    const h = chosenTime.substring(0, 2)
    const m = chosenTime.substring(2)
    const minTime = 0 //00:00
    const maxTime = 1440 //23:55
    const chosenTimeParsed = Number(h) * 60 + Number(m) - 60
    const startTime = Math.min(Math.max(chosenTimeParsed, minTime), maxTime)
    const endTime = Math.min(Math.max(chosenTimeParsed + 125, minTime), maxTime)

    let tt = startTime

    for (let i = 0; tt < endTime; i++) {
      const hh = Math.floor(tt / 60) // getting hours of day in 0-24 format
      const mm = tt % 60 // getting minutes of the hour in 0-55 format
      const timeValue = ("0" + hh).slice(-2) + ("0" + mm).slice(-2)
      opts[i] = { label: timeValue, value: timeValue }
      tt = tt + 5
    }
    return opts
  }

  const depTimeOpts = handleGetTimeOpts(flight.clearedTimeDep)
  const arrTimeOpts = handleGetTimeOpts(flight.clearedTimeArr)

  const showTerminalSection = airportSeasons.find(
    (as) => as.airport === flight.airport
  ).supportsTerminalRequest

  return (
    <CustomModal
      isOpen
      onClose={(e: BtnEvent) => {
        if (e.currentTarget === e.target) {
          onCancel()
        }
      }}
      title={"Flight Availability"}
      ctaText={isHandlingPage ? "Ok" : "Save"}
      onCallback={() => (isHandlingPage ? onCancel() : handleSaveChanges())}
      size="auto"
      onToggle={() => onCancel()}
      fixedHeight="95%"
      fixedWidth="95%"
      showCancel={!isHandlingPage}
    >
      <div>
        <div className="d-flex flight-availability-info ocs-transaction-item-container">
          <TransactionFlight
            idx={0}
            transId={transId}
            flight={flight}
            grcMode={grcMode}
            airportSeasons={airportSeasons}
            editMode={editMode}
            pageGroup={pageGroup}
            codeType={codeType}
            isLastFlight
            customShowLabelConfig={{
              terminal: {
                showLabelSection: showTerminalSection,
                showLabel: true
              },
              parkingLocation: {
                showLabelSection: handleHasFlightsWithParkingLoc(
                  airportSeasons,
                  [{ transId: 0, gcrMode: "FLT", flights: [flight] }]
                ),
                showLabel: true
              },
              serviceTypeAcReg: {
                showLabelSection: true,
                showLabel: handleHasFlightsWithServiceTypeAcReg(
                  airportSeasons,
                  [{ transId: 0, gcrMode: "FLT", flights: [flight] }]
                )
              }
            }}
          />
        </div>

        <div
          className={`d-flex justify-content-between ${
            isHandlingPage ? "wide-matrix" : ""
          } `}
        >
          {arrMatrix && (
            <div
              style={{ width: isHandlingPage && !depMatrix ? "100%" : "49%" }}
            >
              <FlightAvailability
                matrix={arrMatrix}
                timeOptions={arrTimeOpts}
                slotTimeValue={availabilityFlight.clearedTimeArr}
                onChangeView={
                  !isHandlingPage
                    ? (viewMode) =>
                        onChangeAvailability(
                          viewMode,
                          FlightMode.ARRIVAL,
                          availabilityFlight
                        )
                    : undefined
                }
                onChangeTime={
                  !isHandlingPage
                    ? (viewMode, val) =>
                        handleChangeTime(val, FlightMode.ARRIVAL, viewMode)
                    : undefined
                }
              />
            </div>
          )}
          {depMatrix && (
            <div
              style={{ width: isHandlingPage && !arrMatrix ? "100%" : "49%" }}
            >
              <FlightAvailability
                matrix={depMatrix}
                timeOptions={depTimeOpts}
                slotTimeValue={availabilityFlight.clearedTimeDep}
                onChangeView={
                  !isHandlingPage
                    ? (viewMode) =>
                        onChangeAvailability(
                          viewMode,
                          FlightMode.DEPARTURE,
                          availabilityFlight
                        )
                    : undefined
                }
                onChangeTime={
                  !isHandlingPage
                    ? (viewMode, val) =>
                        handleChangeTime(val, FlightMode.DEPARTURE, viewMode)
                    : undefined
                }
              />
            </div>
          )}
        </div>
        <div>
          <header className="ocs-modal-header custom-header">
            <h2 className="ocs-heading-sm-grey">
              {
                "Please note that these results are found using a ground stop of the original length plus/minus two hours"
              }
            </h2>{" "}
          </header>
        </div>
      </div>
    </CustomModal>
  )
}

export default FlightAvailabilityPanel
